import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Circle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, can } from "@/lib/auth";
import { fmtDate, fmtPrice } from "@/lib/format";
import { PageTitle, Loader, Empty, Section } from "@/components/xd/ui";

const CATS = ["", "reviews", "vehicles", "prices", "brokers", "dealers", "inquiries"];
const CAT_PERMS = { reviews: ["vehicles.publish"], vehicles: ["vehicles.manage", "vehicles.publish"], prices: ["prices.set", "financial.view"], brokers: ["brokers.manage"], dealers: ["dealers.manage"], inquiries: ["inquiries.view"] };
const PRIO = { high: "border-s-[var(--xd-red)]", normal: "border-s-zinc-500", low: "border-s-zinc-700" };

export const useUnread = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    const tick = () => api.get("/admin/notifications/unread-count").then((r) => live && setN(r.data.unread)).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    window.addEventListener("xd-notif", tick);
    return () => { live = false; clearInterval(id); window.removeEventListener("xd-notif", tick); };
  }, []);
  return n;
};

const Detail = ({ n }) => {
  const { t } = useLang();
  const d = n.details || {};
  if (n.kind === "availability_changed" && d.to) return <span>{t(`status.${d.from}`)} → {t(`status.${d.to}`)}</span>;
  if (n.kind === "advertised_price_set") return <span className="ltr-num">C: {fmtPrice(d.from)} → {fmtPrice(d.to)}</span>;
  if (n.kind === "price_changed") {
    if (!n.secure) return <span>{(d.fields || []).map((f) => t(`conf.${f}`)).join(", ")}</span>;
    return <span className="ltr-num">{Object.keys(n.secure.to || {}).map((f) => `${f === "asking_price" ? "A" : "B"}: ${fmtPrice(n.secure.from[f])} → ${fmtPrice(n.secure.to[f])}`).join(" · ")}</span>;
  }
  if (n.kind === "broker_name_changed") return <span>{d.old} → {d.new}</span>;
  if (n.kind === "photos_submitted") return <span>{t("notif.photo_count", { n: d.count })}</span>;
  if (n.kind === "callback_request") return <span className="ltr-num">{d.inquiry_code}</span>;
  return null;
};

const Row = ({ n, onOpen, onRead }) => {
  const { t } = useLang();
  const who = n.user_name || n.dealer_name;
  const ref = n.vehicle_code || n.user_uid;
  return (
    <div className={`flex items-start gap-3 border-s-2 ${PRIO[n.priority] || ""} ${n.read ? "bg-transparent" : "bg-white/[0.04]"} px-3 py-3`} data-testid={`notif-${n.id}`}>
      {n.read ? <Circle size={10} className="mt-1.5 shrink-0 text-zinc-700" /> : <Circle size={10} className="mt-1.5 shrink-0 fill-[var(--xd-red)] text-[var(--xd-red)]" data-testid={`notif-unread-${n.id}`} />}
      <button type="button" onClick={() => onOpen(n)} className="flex-1 min-w-0 text-start space-y-1" data-testid={`notif-open-${n.id}`}>
        <p className={`text-sm ${n.read ? "text-zinc-400" : "text-white font-medium"}`}>{t(`notif.${n.kind}`)}</p>
        <p className="text-xs text-zinc-400 flex flex-wrap gap-x-2 gap-y-0.5">
          <span className="text-zinc-500">{t(`notif.cat.${n.category}`)}</span>
          {ref && <span className="ltr-num">{ref}</span>}
          {who && <span>{who}</span>}
          <Detail n={n} />
        </p>
        <p className="text-[11px] text-zinc-600 flex flex-wrap gap-2 items-center">
          <span className="ltr-num">{fmtDate(n.created_at)}</span>
          {n.resolved === true && <span className="text-emerald-400 inline-flex items-center gap-1" data-testid={`notif-resolved-${n.id}`}><CheckCircle2 size={11} />{t("notif.resolved")}</span>}
          {n.resolved === false && <span className="text-amber-300" data-testid={`notif-open-state-${n.id}`}>{t("notif.awaiting")}</span>}
        </p>
      </button>
      {!n.read && <button type="button" onClick={() => onRead(n)} className="shrink-0 text-xs text-zinc-400 hover:text-white min-h-[2.5rem] px-2" data-testid={`notif-read-${n.id}`}>{t("overview.mark_read")}</button>}
    </div>
  );
};

export const NotificationList = ({ compact }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cats = CATS.filter((c) => !c || CAT_PERMS[c].some((p) => can(user, p)));
  const [cat, setCat] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState(null);
  const [more, setMore] = useState(false);
  const [unread, setUnread] = useState(0);
  const fetchPage = useCallback((before) => api.get("/admin/notifications", { params: { category: cat || undefined, unread: unreadOnly || undefined, before, limit: compact ? 5 : 20 } }), [cat, unreadOnly, compact]);
  const load = useCallback(() => fetchPage().then((r) => { setItems(r.data.items); setMore(r.data.has_more); setUnread(r.data.unread); }).catch(() => setItems([])), [fetchPage]);
  useEffect(() => { load(); }, [load]);
  const loadMore = async () => { const r = await fetchPage(items[items.length - 1].created_at); setItems([...items, ...r.data.items]); setMore(r.data.has_more); };
  const markRead = async (n) => { await api.post(`/admin/notifications/${n.id}/read`); setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x))); setUnread((u) => Math.max(0, u - 1)); window.dispatchEvent(new Event("xd-notif")); };
  const open = async (n) => { if (!n.read) await markRead(n); navigate(n.link); };
  const readAll = async () => { await api.post("/admin/notifications/read-all", null, { params: { category: cat || undefined } }); window.dispatchEvent(new Event("xd-notif")); load(); };
  return (
    <Section title={<span className="inline-flex items-center gap-2"><Bell size={16} />{t("notif.title")} {unread > 0 && <span className="ltr-num text-xs bg-[var(--xd-red)] text-white rounded-full px-2 py-0.5" data-testid="notif-unread-count">{unread}</span>}</span>}
      testid={compact ? "admin-alerts" : "notification-center"}
      actions={<div className="flex flex-wrap gap-2">
        {compact ? <Link to="/admin/notifications" className="btn-ghost !min-h-[2.5rem] text-xs" data-testid="notif-view-all">{t("notif.view_all")}</Link>
          : <button type="button" onClick={readAll} disabled={!unread} className="btn-ghost !min-h-[2.5rem] text-xs" data-testid="notif-read-all"><CheckCheck size={14} />{t("notif.read_all")}</button>}
      </div>}>
      {!compact && (
        <div className="flex flex-wrap gap-2" data-testid="notif-filters">
          {cats.map((c) => <button key={c || "all"} type="button" onClick={() => setCat(c)} className={`px-3 min-h-[2.5rem] text-xs border ${cat === c ? "border-[var(--xd-red)] text-white bg-white/5" : "border-white/10 text-zinc-400"}`} data-testid={`notif-filter-${c || "all"}`}>{t(c ? `notif.cat.${c}` : "notif.cat.all")}</button>)}
          <button type="button" onClick={() => setUnreadOnly(!unreadOnly)} className={`px-3 min-h-[2.5rem] text-xs border ${unreadOnly ? "border-[var(--xd-red)] text-white" : "border-white/10 text-zinc-400"}`} data-testid="notif-filter-unread">{t("notif.unread_only")}</button>
        </div>
      )}
      {!items ? <Loader /> : !items.length ? <Empty text={t("overview.no_alerts")} /> : (
        <div className="space-y-1.5" data-testid="notif-list">{items.map((n) => <Row key={n.id} n={n} onOpen={open} onRead={markRead} />)}</div>
      )}
      {!compact && more && <button type="button" onClick={loadMore} className="btn-ghost w-full justify-center" data-testid="notif-load-more">{t("notif.load_more")}</button>}
    </Section>
  );
};

export default function AdminNotifications() {
  const { t } = useLang();
  return (
    <div className="space-y-6">
      <PageTitle eyebrow={t("admin.overview")} title={t("notif.title")} />
      <NotificationList />
    </div>
  );
}
